import { useState, useEffect, useRef } from "react";
import axios from "axios";
import AppShell from "./AppShell";
import { API } from "../App";
import {
  Plus, Trash2, Edit3, Save, X, Receipt, Upload,
  Filter, Search, DollarSign, Calendar, Tag, Building2, FileText
} from "lucide-react";

const CATEGORIES = [
  "Travel", "Software", "Office Supplies", "Meals & Entertainment",
  "Marketing", "Equipment", "Professional Services", "Subscriptions",
  "Insurance", "Utilities", "Rent", "Other"
];

const CURRENCIES = ["USD","EUR","GBP","INR","AUD","CAD","SGD","AED","JPY","CHF","SEK","NOK","DKK","NZD","ZAR","BRL","MXN","HKD","TWD","KRW"];

const getCurrencySymbol = (c) => {
  const m = {USD:"$",EUR:"\u20ac",GBP:"\u00a3",INR:"\u20b9",AUD:"A$",CAD:"C$",SGD:"S$",AED:"\u062f.\u0625",JPY:"\u00a5",CHF:"CHF",SEK:"kr",NOK:"kr",DKK:"kr",NZD:"NZ$",ZAR:"R",BRL:"R$",MXN:"$",HKD:"HK$",TWD:"NT$",KRW:"\u20a9"};
  return m[c] || "$";
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const fileRef = useRef(null);

  const emptyForm = { amount: "", currency: "USD", category: "Other", date: new Date().toISOString().split("T")[0], description: "", vendor: "", client_id: "", notes: "" };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchExpenses();
    axios.get(`${API}/clients`).then(r => setClients(r.data)).catch(() => {});
  }, []);

  const fetchExpenses = (params = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set("category", params.category);
    if (params.client_id) q.set("client_id", params.client_id);
    axios.get(`${API}/expenses?${q}`).then(r => setExpenses(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleSave = async () => {
    if (!form.description.trim() || !form.amount) return;
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (!payload.client_id) delete payload.client_id;
      if (editingId) {
        await axios.put(`${API}/expenses/${editingId}`, payload);
      } else {
        await axios.post(`${API}/expenses`, payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchExpenses();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (exp) => {
    setForm({ amount: exp.amount, currency: exp.currency || "USD", category: exp.category, date: exp.date, description: exp.description, vendor: exp.vendor || "", client_id: exp.client_id || "", notes: exp.notes || "" });
    setEditingId(exp.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await axios.delete(`${API}/expenses/${id}`);
    fetchExpenses();
  };

  const handleReceiptUpload = async (expenseId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/expenses/${expenseId}/receipt`, fd);
      fetchExpenses();
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = expenses.filter(e => {
    if (searchQuery && !e.description.toLowerCase().includes(searchQuery.toLowerCase()) && !(e.vendor || "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterCategory && e.category !== filterCategory) return false;
    if (filterClient && e.client_id !== filterClient) return false;
    return true;
  });

  const totalFiltered = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <AppShell active="expenses">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight" data-testid="expenses-title">Expenses</h1>
            <p className="text-sm text-[#6B7280] mt-0.5">{expenses.length} expense{expenses.length !== 1 ? "s" : ""} tracked</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }}
            className="btn-primary flex items-center gap-2 text-[13px]"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4" /> Add expense
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search expenses..."
              className="input pl-9 text-[13px] w-full"
              data-testid="expense-search"
            />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="input text-[13px] w-auto" data-testid="expense-filter-category">
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="input text-[13px] w-auto" data-testid="expense-filter-client">
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Summary Bar */}
        <div className="bg-[#0A0A0A] text-white p-4 flex items-center justify-between">
          <span className="text-[13px] text-white/60">{filtered.length} expense{filtered.length !== 1 ? "s" : ""} shown</span>
          <span className="text-lg font-bold" data-testid="expenses-total">{getCurrencySymbol("USD")}{totalFiltered.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white border border-[#E5E7EB] p-5 space-y-4" data-testid="expense-form">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[15px] text-[#0A0A0A]">{editingId ? "Edit expense" : "New expense"}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-[#9CA3AF] hover:text-[#0A0A0A]"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Description *</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input text-[13px] w-full" placeholder="What was this for?" data-testid="expense-description" />
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Amount *</label>
                <div className="flex gap-2">
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="input text-[13px] w-20">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="input text-[13px] flex-1" placeholder="0.00" data-testid="expense-amount" />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Date *</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="input text-[13px] w-full" data-testid="expense-date" />
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="input text-[13px] w-full" data-testid="expense-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Vendor / Merchant</label>
                <input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} className="input text-[13px] w-full" placeholder="e.g. Adobe, WeWork" data-testid="expense-vendor" />
              </div>
              <div>
                <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Client (optional)</label>
                <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} className="input text-[13px] w-full" data-testid="expense-client">
                  <option value="">No client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-[#6B7280] font-mono uppercase tracking-wider block mb-1">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="input text-[13px] w-full" placeholder="Optional notes for your records" data-testid="expense-notes" />
            </div>
            <button onClick={handleSave} className="btn-primary flex items-center gap-2 text-[13px]" data-testid="save-expense-btn">
              <Save className="w-3.5 h-3.5" /> {editingId ? "Update" : "Save"} expense
            </button>
          </div>
        )}

        {/* Expense List */}
        <div className="bg-white border border-[#E5E7EB]">
          {loading ? (
            <div className="p-10 text-center text-[#9CA3AF]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="w-10 h-10 text-[#D1D5DB] mx-auto mb-3" />
              <h3 className="font-semibold text-[15px] text-[#0A0A0A] mb-1">
                {expenses.length === 0 ? "No expenses yet" : "No matching expenses"}
              </h3>
              <p className="text-[13px] text-[#6B7280] mb-4">
                {expenses.length === 0 ? "Track your business expenses to see profit & loss." : "Try adjusting your search or filters."}
              </p>
              {expenses.length === 0 && (
                <button onClick={() => setShowForm(true)} className="btn-primary text-[13px] inline-flex items-center gap-2" data-testid="empty-add-expense">
                  <Plus className="w-4 h-4" /> Add your first expense
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {filtered.map(exp => {
                const client = clients.find(c => c.id === exp.client_id);
                return (
                  <div key={exp.id} className="px-4 sm:px-6 py-4 flex items-center gap-4 hover:bg-[#FAFAF9]" data-testid={`expense-row-${exp.id}`}>
                    {/* Category badge */}
                    <div className="hidden sm:flex w-10 h-10 bg-[#F3F4F6] items-center justify-center shrink-0">
                      <Tag className="w-4 h-4 text-[#6B7280]" />
                    </div>
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-[14px] font-semibold text-[#0A0A0A] truncate">{exp.description}</p>
                        <span className="text-[10px] bg-[#F3F4F6] text-[#6B7280] px-1.5 py-0.5 font-mono shrink-0">{exp.category}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[12px] text-[#9CA3AF]">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(exp.date)}</span>
                        {exp.vendor && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{exp.vendor}</span>}
                        {client && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{client.name}</span>}
                        {exp.receipt_url && <span className="text-[#10B981] flex items-center gap-1"><Receipt className="w-3 h-3" />Receipt</span>}
                      </div>
                    </div>
                    {/* Amount */}
                    <span className="text-[15px] font-bold text-[#0A0A0A] shrink-0" data-testid={`expense-amount-${exp.id}`}>
                      {getCurrencySymbol(exp.currency || "USD")}{(exp.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { fileRef.current?.setAttribute("data-expense-id", exp.id); fileRef.current?.click(); }} className="p-1.5 text-[#9CA3AF] hover:text-[#0A0A0A]" title="Upload receipt">
                        <Upload className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleEdit(exp)} className="p-1.5 text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid={`edit-expense-${exp.id}`}>
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(exp.id)} className="p-1.5 text-[#9CA3AF] hover:text-red-600" data-testid={`delete-expense-${exp.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hidden file input for receipt upload */}
        <input
          type="file"
          ref={fileRef}
          className="hidden"
          accept="image/*,.pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            const expenseId = fileRef.current?.getAttribute("data-expense-id");
            if (file && expenseId) handleReceiptUpload(expenseId, file);
            e.target.value = "";
          }}
        />
      </div>
    </AppShell>
  );
}
